import Close from '@mui/icons-material/Close';
import InfoOutline from '@mui/icons-material/InfoOutline';
import {
  Breakpoint,
  Button,
  type ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, ReactNode } from 'react';
import { Column, Row } from '../Layout';
import Switch from '../Switch';

const StyledToggleRow = styled(Row)(({ theme }) => ({
  marginRight: theme.spacing(-theme.spacingSizes.xxs),
}));

type ActionsConfig = {
  confirm: {
    onClick: () => void;
    label?: string;
    variant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
    disabled?: boolean;
  };
  alternateConfirm?: {
    onClick: () => void;
    label?: string;
    variant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
  };
  toggle?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
    tooltip?: string;
  };
  cancel?: {
    onClick?: () => void;
    label?: string;
    variant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
    hidden?: boolean;
  };
};

type Props = {
  actions?: ActionsConfig;
  open?: boolean;
  onClose?: () => void;
  title: string;
  content: ReactNode;
  loading?: boolean;
  maxWidth?: Breakpoint;
  withoutDivider?: boolean;
  fullHeight?: boolean;
  onExited?: () => void;
  customActions?: ReactNode;
  transitionDuration?: DialogProps['transitionDuration'];
};

const ConfirmDialog: FC<Props> = ({
  open,
  onClose,
  title,
  content,
  loading,
  maxWidth = 'xs',
  withoutDivider = false,
  actions,
  fullHeight,
  onExited,
  customActions,
  transitionDuration,
}) => {
  const confirmAction = actions
    ? ({
        label: 'Confirm',
        variant: 'contained',
        color: 'primary',
        ...actions.confirm,
      } as const)
    : null;
  const cancelAction = actions
    ? ({
        label: 'Cancel',
        variant: 'outlined',
        color: 'primary',
        onClick: onClose,
        ...(actions.cancel ?? {}),
      } as const)
    : null;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      transitionDuration={transitionDuration}
      slotProps={{
        ...(fullHeight ? { paper: { style: { height: '100%' } } } : {}),
        transition: { onExited },
      }}
    >
      <DialogTitle>
        <Column spacing="sm" fullWidth>
          <Row centered fullWidth justify="space-between">
            <Typography variant="h4">{title}</Typography>
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Row>
          {!withoutDivider && <Divider />}
        </Column>
      </DialogTitle>

      <DialogContent
        style={
          fullHeight ? { display: 'flex', flexDirection: 'column' } : undefined
        }
      >
        {typeof content === 'string' ? (
          <Typography variant="body2">{content}</Typography>
        ) : (
          content
        )}
      </DialogContent>
      {customActions ? <DialogActions>{customActions}</DialogActions> : null}
      {confirmAction || cancelAction ? (
        <DialogActions>
          <Row fullWidth justify="space-between" centered>
            {!!cancelAction && !cancelAction.hidden ? (
              <Button
                variant={cancelAction.variant}
                color={cancelAction.color}
                size="large"
                onClick={cancelAction.onClick}
                disabled={loading}
              >
                {cancelAction.label}
              </Button>
            ) : null}
            <Row spacing="sm">
              {actions?.toggle ? (
                <StyledToggleRow centered>
                  <Tooltip title={actions.toggle?.tooltip}>
                    <span>
                      <Row spacing="xxs" centered>
                        <Typography
                          variant="h6"
                          color={loading ? 'grey.500' : 'grey.900'}
                        >
                          {actions.toggle?.label}
                        </Typography>
                        {actions.toggle?.tooltip ? (
                          <InfoOutline fontSize="inherit" />
                        ) : null}
                      </Row>
                    </span>
                  </Tooltip>
                  <Switch
                    checked={actions.toggle.checked}
                    onChange={actions.toggle.onChange}
                    disabled={loading}
                  />
                </StyledToggleRow>
              ) : null}
              {actions?.alternateConfirm ? (
                <Button
                  variant={actions.alternateConfirm.variant ?? 'outlined'}
                  color={actions.alternateConfirm.color}
                  size="large"
                  onClick={actions.alternateConfirm.onClick}
                  loading={loading}
                >
                  {actions.alternateConfirm.label}
                </Button>
              ) : null}
              {!!confirmAction && (
                <Button
                  variant={confirmAction.variant}
                  color={confirmAction.color}
                  size="large"
                  onClick={confirmAction.onClick}
                  loading={loading}
                  disabled={confirmAction.disabled}
                >
                  {confirmAction.label}
                </Button>
              )}
            </Row>
          </Row>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default ConfirmDialog;
